import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

const ServiceGroupParasocialSettings = ({ groupId, groupName }) => {
  const [settings, setSettings] = useState(null);
  const [effectiveSettings, setEffectiveSettings] = useState(null);
  const [platformDefaults, setPlatformDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (groupId) {
      fetchSettings();
    }
  }, [groupId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const [settingsRes, effectiveRes] = await Promise.all([
        api.get(`/service-groups/${groupId}/parasocial`),
        api.get(`/service-groups/${groupId}/parasocial/effective`)
      ]);

      setSettings(settingsRes.data.parasocialOverrides || { overrideEnabled: false });
      setPlatformDefaults(settingsRes.data.platformDefaults || {});
      setEffectiveSettings(effectiveRes.data.effectiveSettings || {});
    } catch (err) {
      console.error('Error fetching parasocial settings:', err);
      setError(err.response?.data?.error || 'Failed to load parasocial settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOverride = async (enabled) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.put(`/service-groups/${groupId}/parasocial/toggle-override`, { enabled });

      setSettings(prev => ({ ...prev, overrideEnabled: enabled }));
      setSuccess(`Service group override ${enabled ? 'enabled' : 'disabled'}`);
      fetchSettings();
    } catch (err) {
      console.error('Error toggling override:', err);
      setError(err.response?.data?.error || 'Failed to toggle override');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSettings = async (updates) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const res = await api.put(`/service-groups/${groupId}/parasocial`, {
        overrideEnabled: true,
        ...updates
      });

      setSettings(res.data.parasocialOverrides);
      setSuccess('Settings updated successfully');
      fetchSettings();
    } catch (err) {
      console.error('Error updating settings:', err);
      setError(err.response?.data?.error || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm('Reset service group parasocial settings to platform defaults?')) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.delete(`/service-groups/${groupId}/parasocial/override`);

      setSuccess('Reset to platform defaults');
      fetchSettings();
    } catch (err) {
      console.error('Error resetting settings:', err);
      setError(err.response?.data?.error || 'Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  if (!groupId) {
    return <div style={styles.container}>Select a service group to configure parasocial settings.</div>;
  }

  if (loading) {
    return <div style={styles.container}>Loading parasocial settings...</div>;
  }

  const isOverrideEnabled = settings?.overrideEnabled;

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Parasocial Detection for {groupName || 'Service Group'}</h3>
      <p style={styles.subtitle}>
        Configure parasocial detection overrides for this service group.
        Use this to adjust sensitivity for specific populations (e.g., stricter for vulnerable groups).
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Current Effective Settings */}
      <div style={styles.effectiveBox}>
        <h4 style={styles.effectiveTitle}>Currently Active Settings</h4>
        <div style={styles.effectiveSource}>
          Source: <strong>
            {effectiveSettings?.source === 'service_group_override'
              ? 'Service Group Override'
              : 'Platform Default'}
          </strong>
        </div>
        {effectiveSettings?.enabled === false ? (
          <div style={styles.disabled}>Parasocial detection is disabled for this group</div>
        ) : (
          <div style={styles.effectiveDetails}>
            <div>Attachment Detection: <strong>{effectiveSettings?.attachmentLanguageDetection !== false ? 'On' : 'Off'}</strong></div>
            <div>Engagement Tracking: <strong>{effectiveSettings?.engagementTracking !== false ? 'On' : 'Off'}</strong></div>
            <div>Disclosure Mode: <strong>{effectiveSettings?.transparency?.disclosureMode || 'moderate'}</strong></div>
            <div>Real-Time Alerts: <strong>{effectiveSettings?.alerts?.realTimeAlerts || 'critical_only'}</strong></div>
            {effectiveSettings?.thresholds && (
              <>
                <div>Score Threshold: {effectiveSettings.thresholds.attachmentScoreThreshold ?? 0.4}</div>
                <div>Signal Count Threshold: {effectiveSettings.thresholds.signalCountThreshold ?? 3}</div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Override Toggle */}
      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>Service Group Override</h4>
        <label style={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={isOverrideEnabled}
            onChange={(e) => handleToggleOverride(e.target.checked)}
            disabled={saving}
            style={styles.checkbox}
          />
          <span style={styles.toggleText}>
            {isOverrideEnabled ? 'Override Active' : 'Using Platform Defaults'}
          </span>
        </label>
        <p style={styles.description}>
          When enabled, you can set custom parasocial detection settings for this service group.
        </p>
      </div>

      {/* Override Settings (only shown when override is enabled) */}
      {isOverrideEnabled && (
        <>
          {/* Feature Toggles */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Detection Features</h4>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings?.attachmentLanguageDetection !== false}
                  onChange={(e) => handleUpdateSettings({
                    attachmentLanguageDetection: e.target.checked
                  })}
                  disabled={saving}
                  style={styles.checkbox}
                />
                Attachment Language Detection
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings?.engagementTracking !== false}
                  onChange={(e) => handleUpdateSettings({
                    engagementTracking: e.target.checked
                  })}
                  disabled={saving}
                  style={styles.checkbox}
                />
                Engagement Pattern Tracking
              </label>
            </div>
          </div>

          {/* Thresholds */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Detection Thresholds</h4>
            <div style={styles.formGrid}>
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Attachment Score Threshold (0-1)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={settings?.thresholds?.attachmentScoreThreshold ?? platformDefaults?.thresholds?.attachmentScoreThreshold ?? 0.4}
                  onChange={(e) => handleUpdateSettings({
                    thresholds: { attachmentScoreThreshold: parseFloat(e.target.value) }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
                <span style={styles.fieldHint}>
                  Platform default: {platformDefaults?.thresholds?.attachmentScoreThreshold ?? 0.4}
                </span>
              </div>
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Signal Count Threshold</label>
                <input
                  type="number"
                  min="1"
                  value={settings?.thresholds?.signalCountThreshold ?? platformDefaults?.thresholds?.signalCountThreshold ?? 3}
                  onChange={(e) => handleUpdateSettings({
                    thresholds: { signalCountThreshold: parseInt(e.target.value) }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
                <span style={styles.fieldHint}>
                  Platform default: {platformDefaults?.thresholds?.signalCountThreshold ?? 3}
                </span>
              </div>
            </div>

            <h5 style={styles.subTitle}>High Engagement Thresholds</h5>
            <div style={styles.formGrid}>
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Sessions/Day (High)</label>
                <input
                  type="number"
                  min="1"
                  value={settings?.thresholds?.engagement?.highFrequency?.sessionsPerDay ?? 5}
                  onChange={(e) => handleUpdateSettings({
                    thresholds: {
                      engagement: {
                        highFrequency: {
                          sessionsPerDay: parseInt(e.target.value),
                          hoursPerWeek: settings?.thresholds?.engagement?.highFrequency?.hoursPerWeek ?? 15
                        }
                      }
                    }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
              </div>
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Hours/Week (High)</label>
                <input
                  type="number"
                  min="1"
                  value={settings?.thresholds?.engagement?.highFrequency?.hoursPerWeek ?? 15}
                  onChange={(e) => handleUpdateSettings({
                    thresholds: {
                      engagement: {
                        highFrequency: {
                          sessionsPerDay: settings?.thresholds?.engagement?.highFrequency?.sessionsPerDay ?? 5,
                          hoursPerWeek: parseInt(e.target.value)
                        }
                      }
                    }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
              </div>
            </div>

            <h5 style={styles.subTitle}>Critical Engagement Thresholds</h5>
            <div style={styles.formGrid}>
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Sessions/Day (Critical)</label>
                <input
                  type="number"
                  min="1"
                  value={settings?.thresholds?.engagement?.critical?.sessionsPerDay ?? 10}
                  onChange={(e) => handleUpdateSettings({
                    thresholds: {
                      engagement: {
                        critical: {
                          sessionsPerDay: parseInt(e.target.value),
                          hoursPerWeek: settings?.thresholds?.engagement?.critical?.hoursPerWeek ?? 30
                        }
                      }
                    }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
              </div>
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Hours/Week (Critical)</label>
                <input
                  type="number"
                  min="1"
                  value={settings?.thresholds?.engagement?.critical?.hoursPerWeek ?? 30}
                  onChange={(e) => handleUpdateSettings({
                    thresholds: {
                      engagement: {
                        critical: {
                          sessionsPerDay: settings?.thresholds?.engagement?.critical?.sessionsPerDay ?? 10,
                          hoursPerWeek: parseInt(e.target.value)
                        }
                      }
                    }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
              </div>
            </div>
          </div>

          {/* Transparency Settings */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Disclosure Mode</h4>
            <select
              value={settings?.transparency?.disclosureMode || ''}
              onChange={(e) => handleUpdateSettings({
                transparency: { disclosureMode: e.target.value }
              })}
              disabled={saving}
              style={styles.select}
            >
              <option value="">Use Platform Default</option>
              <option value="conservative">Conservative (less frequent disclosures)</option>
              <option value="moderate">Moderate (balanced)</option>
              <option value="proactive">Proactive (more frequent disclosures)</option>
            </select>
          </div>

          {/* Alert Settings */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Alert Configuration</h4>
            <select
              value={settings?.alerts?.realTimeAlerts || ''}
              onChange={(e) => handleUpdateSettings({
                alerts: { realTimeAlerts: e.target.value }
              })}
              disabled={saving}
              style={styles.select}
            >
              <option value="">Use Platform Default</option>
              <option value="disabled">Disabled</option>
              <option value="critical_only">Critical Only</option>
              <option value="escalating_trend">Escalating Trends</option>
              <option value="all">All Detections</option>
            </select>
          </div>

          {/* Disable Option */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Disable Parasocial Detection</h4>
            <label style={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={settings?.parasocialDisabled === true}
                onChange={(e) => handleUpdateSettings({
                  parasocialDisabled: e.target.checked,
                  disabledReason: e.target.checked ? 'Disabled by Clinical Supervisor' : undefined
                })}
                disabled={saving}
                style={styles.checkbox}
              />
              <span style={{ color: settings?.parasocialDisabled ? '#dc2626' : 'inherit' }}>
                Completely disable parasocial detection for this service group
              </span>
            </label>
            {settings?.parasocialDisabled && (
              <input
                type="text"
                placeholder="Reason for disabling (optional)"
                value={settings?.disabledReason || ''}
                onChange={(e) => handleUpdateSettings({ disabledReason: e.target.value })}
                disabled={saving}
                style={{ ...styles.input, marginTop: '10px' }}
              />
            )}
          </div>

          {/* Reset Button */}
          <div style={styles.section}>
            <button
              onClick={handleResetToDefaults}
              disabled={saving}
              style={styles.resetButton}
            >
              Reset to Platform Defaults
            </button>
          </div>
        </>
      )}

      {/* Platform Defaults Reference */}
      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>Platform Defaults Reference</h4>
        <div style={styles.defaultsGrid}>
          <div style={styles.defaultItem}>
            <strong>Attachment Detection</strong>
            <span>{platformDefaults?.attachmentLanguageDetection !== false ? 'Enabled' : 'Disabled'}</span>
          </div>
          <div style={styles.defaultItem}>
            <strong>Engagement Tracking</strong>
            <span>{platformDefaults?.engagementTracking !== false ? 'Enabled' : 'Disabled'}</span>
          </div>
          <div style={styles.defaultItem}>
            <strong>Score Threshold</strong>
            <span>{platformDefaults?.thresholds?.attachmentScoreThreshold ?? 0.4}</span>
          </div>
          <div style={styles.defaultItem}>
            <strong>Signal Threshold</strong>
            <span>{platformDefaults?.thresholds?.signalCountThreshold ?? 3}</span>
          </div>
          <div style={styles.defaultItem}>
            <strong>Disclosure Mode</strong>
            <span>{platformDefaults?.transparency?.disclosureMode || 'moderate'}</span>
          </div>
          <div style={styles.defaultItem}>
            <strong>Real-Time Alerts</strong>
            <span>{platformDefaults?.alerts?.realTimeAlerts || 'critical_only'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '5px',
    color: '#333'
  },
  subtitle: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '20px'
  },
  error: {
    padding: '10px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    borderRadius: '6px',
    marginBottom: '15px',
    fontSize: '14px'
  },
  success: {
    padding: '10px',
    backgroundColor: '#d1fae5',
    color: '#059669',
    borderRadius: '6px',
    marginBottom: '15px',
    fontSize: '14px'
  },
  effectiveBox: {
    padding: '15px',
    backgroundColor: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    marginBottom: '20px'
  },
  effectiveTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#92400e'
  },
  effectiveSource: {
    fontSize: '13px',
    marginBottom: '8px'
  },
  effectiveDetails: {
    fontSize: '13px',
    color: '#555',
    lineHeight: '1.6'
  },
  disabled: {
    color: '#dc2626',
    fontWeight: '500'
  },
  section: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#444'
  },
  subTitle: {
    fontSize: '13px',
    fontWeight: '600',
    marginTop: '15px',
    marginBottom: '10px',
    color: '#555'
  },
  description: {
    fontSize: '13px',
    color: '#666',
    marginTop: '8px'
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  toggleText: {
    fontSize: '14px',
    fontWeight: '500',
    marginLeft: '8px'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px'
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  fieldLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#555'
  },
  fieldHint: {
    fontSize: '11px',
    color: '#888'
  },
  input: {
    padding: '8px',
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #ccc'
  },
  select: {
    width: '100%',
    padding: '8px',
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    backgroundColor: '#fff'
  },
  resetButton: {
    padding: '8px 16px',
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  defaultsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
    marginTop: '10px'
  },
  defaultItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '10px',
    backgroundColor: '#fff',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    fontSize: '12px'
  }
};

export default ServiceGroupParasocialSettings;
